import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  Chip,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  RestartAlt as ResetIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axiosInstance from '../utils/axios';

const RolePermissions = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [permissions, setPermissions] = useState({});
  const [selectedRole, setSelectedRole] = useState('operator');

  const roles = [
    { value: 'curator', label: 'Куратор', color: 'error', description: 'Полный доступ ко всем функциям' },
    { value: 'operator', label: 'Оператор', color: 'primary', description: 'Управление системой' },
    { value: 'courier', label: 'Курьер', color: 'success', description: 'Просмотр заказов и адресов' }
  ];

  const resources = [
    {
      name: 'users',
      label: 'Пользователи',
      actions: ['view', 'create', 'edit', 'delete']
    },
    {
      name: 'couriers',
      label: 'Курьеры',
      actions: ['view', 'create', 'edit', 'delete']
    },
    {
      name: 'staff',
      label: 'Сотрудники',
      actions: ['view', 'create']
    },
    {
      name: 'addresses',
      label: 'Адреса',
      actions: ['view', 'create', 'edit', 'delete']
    },
    {
      name: 'orders',
      label: 'Заказы',
      actions: ['view', 'create', 'edit', 'delete']
    },
    {
      name: 'cities',
      label: 'Города',
      actions: ['view', 'create', 'edit', 'delete']
    },
    {
      name: 'districts',
      label: 'Районы',
      actions: ['view', 'create', 'edit', 'delete']
    },
    {
      name: 'products',
      label: 'Товары',
      actions: ['view', 'create', 'edit', 'delete']
    },
    {
      name: 'packagings',
      label: 'Фасовки',
      actions: ['view', 'create', 'edit', 'delete']
    },
    {
      name: 'telegramBots',
      label: 'Telegram боты',
      actions: ['view', 'create', 'edit', 'delete', 'settings']
    },
    {
      name: 'telegramUsers',
      label: 'Telegram пользователи',
      actions: ['view', 'edit', 'block', 'balance']
    },
    {
      name: 'telegramChannels',
      label: 'Telegram каналы',
      actions: ['view', 'create', 'edit', 'delete']
    },
    {
      name: 'stats',
      label: 'Статистика',
      actions: ['view', 'courierStats', 'cityStats', 'productStats']
    },
    {
      name: 'salaries',
      label: 'Зарплаты',
      actions: ['view', 'edit']
    },
    {
      name: 'withdrawals',
      label: 'Выводы средств',
      actions: ['view', 'approve']
    },
    {
      name: 'inventory',
      label: 'Инвентарь',
      actions: ['view', 'edit']
    },
    {
      name: 'bugTracker',
      label: 'Баг-трекер',
      actions: ['view', 'create', 'edit']
    },
    {
      name: 'settings',
      label: 'Настройки',
      actions: ['view', 'edit']
    },
    {
      name: 'roles',
      label: 'Управление ролями',
      actions: ['view', 'edit']
    },
    {
      name: 'activityLogs',
      label: 'Логи активности',
      actions: ['view', 'export', 'delete']
    },
    {
      name: 'broadcasts',
      label: 'Рассылки',
      actions: ['view', 'create', 'edit', 'delete']
    }
  ];

  const actionLabels = {
    view: 'Просмотр',
    create: 'Создание',
    edit: 'Редактирование',
    delete: 'Удаление',
    settings: 'Настройки',
    block: 'Блокировка',
    balance: 'Баланс',
    courierStats: 'Статистика курьеров',
    cityStats: 'Статистика городов',
    productStats: 'Статистика товаров',
    approve: 'Одобрение',
    export: 'Экспорт'
  };

  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/role-permissions');
      const permissionsData = {};
      
      response.data.forEach(roleData => {
        permissionsData[roleData.role] = roleData.permissions;
      });
      
      setPermissions(permissionsData);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      enqueueSnackbar('Ошибка загрузки прав доступа', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (resource, action, value) => {
    setPermissions(prev => ({
      ...prev,
      [selectedRole]: {
        ...prev[selectedRole],
        [resource]: {
          ...prev[selectedRole]?.[resource],
          [action]: value
        }
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosInstance.put(`/role-permissions/${selectedRole}`, {
        permissions: permissions[selectedRole]
      });
      
      enqueueSnackbar('Права доступа сохранены', { variant: 'success' });
    } catch (error) {
      console.error('Error saving permissions:', error);
      enqueueSnackbar(error.response?.data?.message || 'Ошибка сохранения прав доступа', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Вы уверены что хотите сбросить права к дефолтным?')) {
      return;
    }

    setSaving(true);
    try {
      await axiosInstance.post(`/role-permissions/${selectedRole}/reset`);
      await fetchPermissions();
      
      enqueueSnackbar('Права доступа сброшены к дефолтным', { variant: 'success' });
    } catch (error) {
      console.error('Error resetting permissions:', error);
      enqueueSnackbar(error.response?.data?.message || 'Ошибка сброса прав доступа', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const currentPermissions = permissions[selectedRole] || {};

  return (
    <Container maxWidth="xl" sx={{ pb: 4 }}>
      {/* Header */}
      <Paper
        elevation={4}
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 3,
          p: 3,
          mb: 3,
          color: 'text.primary'
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <Avatar sx={{ bgcolor: 'action.selected', color: 'primary.main', mr: 2, width: 56, height: 56 }}>
              <SecurityIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                Управление правами доступа
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Настройка прав для ролей пользователей
              </Typography>
            </Box>
          </Box>
          <Tooltip title="Обновить">
            <IconButton
              onClick={fetchPermissions}
              disabled={loading}
              sx={{
                color: 'text.primary',
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'action.selected', color: 'primary.main' }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Role Selection */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {roles.map((role) => (
              <Grid item xs={12} md={4} key={role.value}>
                <Card
                  onClick={() => setSelectedRole(role.value)}
                  sx={{
                    cursor: role.value === 'curator' ? 'not-allowed' : 'pointer',
                    opacity: role.value === 'curator' ? 0.6 : 1,
                    border: selectedRole === role.value ? `3px solid ${theme.palette[role.color].main}` : '1px solid #e0e0e0',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: role.value === 'curator' ? 'none' : 'translateY(-4px)',
                      boxShadow: role.value === 'curator' ? 1 : 4
                    }
                  }}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Chip
                        label={role.label}
                        color={role.color}
                        sx={{ fontWeight: 'bold' }}
                      />
                      {selectedRole === role.value && (
                        <CheckIcon color={role.color} />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {role.description}
                    </Typography>
                    {role.value === 'curator' && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Права куратора нельзя изменить
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {selectedRole === 'curator' ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              Куратор имеет полный доступ ко всем функциям системы. Эти права нельзя изменить.
            </Alert>
          ) : (
            <>
              {/* Actions */}
              <Box display="flex" gap={2} mb={3}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                  size="large"
                >
                  {saving ? 'Сохранение...' : 'Сохранить изменения'}
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<ResetIcon />}
                  onClick={handleReset}
                  disabled={saving}
                  size="large"
                >
                  Сбросить к дефолтным
                </Button>
              </Box>

              {/* Permissions */}
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight="bold" mb={3}>
                  Настройка прав для роли: {roles.find(r => r.value === selectedRole)?.label}
                </Typography>

                {resources.map((resource, index) => (
                  <Accordion key={resource.name} defaultExpanded={index === 0}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography fontWeight="600">{resource.label}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        {resource.actions.map((action) => (
                          <Grid item xs={12} sm={6} md={3} key={action}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={currentPermissions[resource.name]?.[action] || false}
                                  onChange={(e) => handlePermissionChange(resource.name, action, e.target.checked)}
                                  color="primary"
                                />
                              }
                              label={actionLabels[action] || action}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Paper>
            </>
          )}
        </>
      )}
    </Container>
  );
};

export default RolePermissions;
